import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { DeleteButton } from "./DeleteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const InboxTab = ({ headers }) => {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [kind, setKind] = useState("all");

  useEffect(() => {
    axios.get(`${API}/admin/messages`, { headers }).then((r) => setItems(r.data)).catch(() => toast.error("Could not load messages."));
  }, [headers]);

  const send = async (m) => {
    if (reply.trim().length < 2) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/messages/${m.id}/reply`, { body: reply }, { headers });
      setItems((l) => l.map((x) => (x.id === m.id ? r.data : x)));
      setReply("");
      toast.success(`Reply sent to ${m.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reply.");
    } finally {
      setSending(false);
    }
  };

  if (items === null) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#5f5e5a]" /></div>;
  if (!items.length) return <p data-testid="inbox-empty" className="mt-16 text-center text-[#5f5e5a] font-mono-d text-[11px] uppercase tracking-[0.2em]">No messages yet.</p>;
  const counts = { all: items.length, contact: items.filter((m) => (m.kind || "contact") === "contact").length, wholesale: items.filter((m) => m.kind === "wholesale").length };
  const rows = kind === "all" ? items : items.filter((m) => (m.kind || "contact") === kind);

  return (
    <div className="mt-8">
      <div data-testid="inbox-filters" className="flex flex-wrap gap-2 mb-4">
        {[["all", "All"], ["contact", "Contact"], ["wholesale", "Stockist applications"]].map(([id, label]) => (
          <button key={id} type="button" data-testid={`inbox-filter-${id}`} onClick={() => setKind(id)} className={`h-9 px-4 rounded-full border font-mono-d text-[10px] uppercase tracking-[0.2em] transition-colors ${kind === id ? "bg-[#0a0a0b] text-white border-[#0a0a0b]" : "border-[#e3e2dc] hover:border-[#0a0a0b]"}`}>
            {label} <span className={kind === id ? "text-[#ff2a85]" : "text-[#5f5e5a]"}>{counts[id]}</span>
          </button>
        ))}
      </div>
      {!rows.length && <p data-testid="inbox-filter-empty" className="py-16 text-center text-[#5f5e5a] font-mono-d text-[11px] uppercase tracking-[0.2em]">Nothing here yet.</p>}
    <div data-testid="admin-inbox" className="border border-[#e3e2dc] rounded-2xl overflow-hidden bg-[#faf9f6] divide-y divide-[#e3e2dc]">
      {rows.map((m) => {
        const open = openId === m.id;
        return (
          <div key={m.id} data-testid={`message-${m.id}`}>
            <div className="flex items-center pr-3">
            <button onClick={() => { setOpenId(open ? null : m.id); setReply(""); }} className="flex-1 min-w-0 text-left px-5 py-4 flex items-center gap-4 hover:bg-[#ffffff] transition-colors">
              <span className={`h-2 w-2 rounded-full shrink-0 ${m.status === "new" ? "bg-[#ff2a85]" : "bg-[#e3e2dc]"}`} />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{m.kind === "wholesale" && <span className="font-mono-d text-[9px] uppercase tracking-[0.15em] text-white bg-[#007a3d] px-2 py-0.5 rounded-full mr-2 align-middle">Stockist</span>}{m.subject || "(no subject)"} <span className="text-[#5f5e5a] font-normal">· {m.name}</span></p>
                <p className="text-xs text-[#5f5e5a] truncate">{m.email} · {fmt(m.created_at)}{m.replies?.length ? ` · ${m.replies.length} repl${m.replies.length > 1 ? "ies" : "y"}` : ""}</p>
              </div>
              <span className={`font-mono-d text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${m.status === "replied" ? "bg-[#0a0a0b] text-white" : m.status === "reply_failed" ? "bg-[#ff2a85]/10 text-[#ff2a85]" : "bg-[#f3f2ec] text-[#5f5e5a]"}`}>{m.status.replace("_", " ")}</span>
            </button>
            <DeleteButton size="sm" testId={`message-delete-${m.id}`} title="Delete message?" description={`"${m.subject || "(no subject)"}" from ${m.name} and any replies will be removed from the inbox.`}
              onConfirm={async () => { await axios.delete(`${API}/admin/messages/${m.id}`, { headers }); setItems((l) => l.filter((x) => x.id !== m.id)); toast.success("Message deleted"); }} />
            </div>
            {open && (
              <div className="px-5 pb-5 space-y-4">
                {m.kind === "wholesale" && m.details ? <WholesaleDetails d={m.details} /> : <p className="text-sm leading-relaxed whitespace-pre-wrap bg-[#ffffff] border border-[#e3e2dc] rounded-xl p-4">{m.message}</p>}
                {m.replies?.map((r, i) => (
                  <p key={i} className="text-sm leading-relaxed whitespace-pre-wrap bg-[#0a0a0b] text-white rounded-xl p-4 ml-8">
                    <span className="block font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#ff2a85] mb-2">You · {fmt(r.at)} · {r.status}</span>{r.body}
                  </p>
                ))}
                <div className="flex flex-col sm:flex-row gap-2 ml-8">
                  <textarea data-testid={`reply-input-${m.id}`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={`Reply to ${m.name}…`} className="flex-1 min-h-[88px] p-3 rounded-xl bg-[#ffffff] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60" />
                  <button data-testid={`reply-send-${m.id}`} onClick={() => send(m)} disabled={sending} className="h-11 sm:self-end px-5 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 disabled:opacity-60">
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send reply
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
    </div>
  );
};

const DETAIL_LABELS = [
  ["business_name", "Business"], ["business_type", "Type"], ["contact_name", "Contact"], ["role", "Role"], ["phone", "Phone"], ["website", "Website"],
  ["instagram", "Social"], ["years_in_business", "Years in business"], ["locations", "Locations"], ["country", "Country"], ["city", "City"], ["address", "Address"],
  ["annual_revenue", "Annual revenue"], ["monthly_volume", "Monthly volume"], ["launch_timeline", "Launch"], ["sales_channels", "Channels"], ["brands_carried", "Brands carried"],
  ["where_sell", "Where they'll sell"], ["customer_profile", "Customer"], ["why_deen", "Why Deen District"], ["notes", "Notes"],
];
const LONG = new Set(["where_sell", "customer_profile", "why_deen", "notes", "brands_carried", "sales_channels", "address"]);

const WholesaleDetails = ({ d }) => (
  <dl data-testid="wholesale-details" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 bg-[#ffffff] border border-[#e3e2dc] rounded-xl p-4 text-sm">
    {DETAIL_LABELS.filter(([k]) => Array.isArray(d[k]) ? d[k].length : d[k]).map(([k, label]) => (
      <div key={k} className={LONG.has(k) ? "sm:col-span-2 lg:col-span-3" : ""}>
        <dt className="font-mono-d text-[9px] uppercase tracking-[0.2em] text-[#5f5e5a]">{label}</dt>
        <dd className="mt-0.5 whitespace-pre-wrap break-words">{k === "website" && /^https?:/.test(d[k]) ? <a href={d[k]} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#ff2a85]">{d[k]}</a> : Array.isArray(d[k]) ? d[k].join(" · ") : d[k]}</dd>
      </div>
    ))}
  </dl>
);
